import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const ModulesContext = createContext({
  enabledModules: new Set(),
  activeModules: new Set(),
  availableModules: new Set(),
  lockedModules: new Set(),
  loading: true,
  refresh: () => {},
});

export function ModulesProvider({ children }) {
  const [enabledModules, setEnabledModules] = useState(new Set());
  const [activeModules, setActiveModules] = useState(new Set());
  const [availableModules, setAvailableModules] = useState(new Set());
  const [lockedModules, setLockedModules] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // 2026-07-26 (KB-004) — Skip the modules probe when unauthenticated so
    // anonymous landing-page visits never emit a 401 to /api/modules/accessible.
    // Anonymous visitors see all marketing content (all modules) by default.
    const hasToken = typeof localStorage !== 'undefined' && localStorage.getItem('token');
    if (!hasToken) {
      setEnabledModules(new Set(['__all__']));
      setActiveModules(new Set(['__all__']));
      setAvailableModules(new Set());
      setLockedModules(new Set());
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/api/modules/accessible`);
      const active = new Set(res.data.active || res.data.accessible || []);
      const available = new Set(res.data.available || []);
      const locked = new Set(res.data.locked || []);
      setActiveModules(active);
      setAvailableModules(available);
      setLockedModules(locked);
      setEnabledModules(active);
    } catch {
      setEnabledModules(new Set(['__all__']));
      setActiveModules(new Set(['__all__']));
      setAvailableModules(new Set());
      setLockedModules(new Set());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <ModulesContext.Provider value={{ enabledModules, activeModules, availableModules, lockedModules, loading, refresh }}>
      {children}
    </ModulesContext.Provider>
  );
}

export function useModules() {
  return useContext(ModulesContext);
}

export function isModuleEnabled(enabledModules, moduleId) {
  if (!moduleId) return true;
  if (enabledModules.has('__all__')) return true;
  return enabledModules.has(moduleId);
}

export function getModuleState(activeModules, availableModules, lockedModules, moduleId) {
  if (!moduleId) return 'active';
  if (activeModules.has('__all__')) return 'active';
  if (activeModules.has(moduleId)) return 'active';
  if (availableModules.has(moduleId)) return 'available';
  if (lockedModules.has(moduleId)) return 'locked';
  return 'active'; // no moduleId restriction = always show
}
